import { MESSAGE_COLlECTION } from "@fb/const";
import { db } from "@fb/db";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { create } from "zustand";

import { AssistantMessageType } from "@components/AssistantChat/type";

interface MessageState {
  messages: Record<string, AssistantMessageType[]>;
  loading: boolean;
  error: string | null;
  fetchMessages: (conversationId: string) => Promise<void>;
  clearMessages: () => void;
  addMessage: ({
    conversationId,
    message,
  }: {
    conversationId: string;
    message: AssistantMessageType;
  }) => void;
  updateMessage: ({
    conversationId,
    messageId,
    content,
  }: {
    conversationId: string;
    messageId: string;
    content: string;
  }) => void;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  messages: {},
  loading: false,
  error: null,
  fetchMessages: async (conversationId: string) => {
    try {
      set({ loading: true, error: null });

      const q = query(
        collection(db, MESSAGE_COLlECTION),
        where("conversationId", "==", conversationId),
        orderBy("date", "asc")
      );

      const querySnapshot = await getDocs(q);
      const messages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AssistantMessageType[];

      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: messages,
        },
        loading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to fetch messages",
        loading: false,
      });
    }
  },
  clearMessages: () => {
    set({ messages: {}, loading: false, error: null });
  },
  addMessage: ({ conversationId, message }) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: [...(state.messages[conversationId] || []), message],
      },
    }));
  },
  updateMessage: ({ conversationId, messageId, content }) => {
    set((state) => {
      const conversationMessages = state.messages[conversationId] || [];
      const messageIndex = conversationMessages.findIndex(
        (message) => message.id === messageId
      );

      if (messageIndex === -1) {
        return state;
      }

      const updatedMessages = [...conversationMessages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content,
      };

      return {
        messages: {
          ...state.messages,
          [conversationId]: updatedMessages,
        },
      };
    });
  },
}));
